import { GOOGLE_API_KEY, GOOGLE_MAP_ID } from "@/constants/endpoints";
import { EstablishmentLote, LotePolygonPoint } from "@/services/establishments.service";
import { AppColors } from "@/styles/colors";
import { APIProvider, Map, Polygon } from "@vis.gl/react-google-maps";
import { Box, CloseButton, Dialog, Flex, Portal, Text } from "@chakra-ui/react";
import { Dispatch, SetStateAction, useMemo } from "react";
import { LightMode } from "../ui/color-mode";
import { CenterLock, DEFAULT_ZOOM, MIN_ZOOM } from "./map/map-utils";

const computeCentroid = (points: LotePolygonPoint[]) => {
  //Calcula el centroide para ubicar el mapa en el centro del polígono
  if (!points.length) return { lat: 0, lng: 0 };
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.latitud, lng: acc.lng + p.longitud }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
};

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  lote: EstablishmentLote | null;
  establishmentName?: string;
  establishmentLotes?: EstablishmentLote[];
}

export const VerLoteMapModal = ({ open, setOpen, lote, establishmentName, establishmentLotes }: Props) => {
  const puntos = lote?.coordenadasPoligono?.puntos ?? [];

  const center = useMemo(() => computeCentroid(puntos), [puntos]);
  const path = useMemo(
    () => puntos.map((p) => ({ lat: p.latitud, lng: p.longitud })),
    [puntos],
  );

  if (!lote) return null;

  const { id, hectareas } = lote;

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content maxWidth={{ base: "95vw", md: "720px", "2xl": "860px" }} width="100%">
              <Dialog.Header paddingY="14px" paddingX="24px">
                <Dialog.Title fontWeight={600} color={AppColors.GRAY_TEXT} fontSize="18px">
                  Ver Lote
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="0" paddingY="0">
                <Flex flexDir="column" paddingX="24px" paddingTop="10px" paddingBottom="4px" gap="6px">
                  {establishmentName && (
                    <Text fontWeight={600} fontSize="15px" color={AppColors.GRAY_TEXT}>
                      {establishmentName}
                    </Text>
                  )}
                  <Flex gap="24px" alignItems="center" flexWrap="wrap" paddingBottom="6px">
                    <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                      ID Agrobit: {id}
                    </Text>
                    <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                      {hectareas ?? "-"} ha
                    </Text>
                  </Flex>
                </Flex>

                <Box padding="20px">
                  <Box
                    height={{ base: "260px", md: "320px", "2xl": "440px" }}
                    width="100%"
                    overflow="hidden"
                    borderRadius="8px"
                  >
                    <APIProvider apiKey={GOOGLE_API_KEY}>
                      <Map
                        defaultCenter={center}
                        defaultZoom={DEFAULT_ZOOM}
                        minZoom={MIN_ZOOM}
                        disableDefaultUI
                        gestureHandling="greedy"
                        style={{ width: "100%", height: "100%" }}
                        mapId={GOOGLE_MAP_ID}
                      >
                        <CenterLock center={center} />
                        {establishmentLotes?.map((estLote) => {
                          const estPath = estLote.coordenadasPoligono?.puntos?.map((p) => ({
                            lat: p.latitud,
                            lng: p.longitud,
                          }));
                          if (!estPath?.length) return null;
                          return (
                            <Polygon
                              key={`est-${estLote.id}`}
                              paths={estPath}
                              strokeColor={AppColors.LIGHT_GRAY_TEXT}
                              strokeOpacity={0.9}
                              strokeWeight={2}
                              fillColor={AppColors.LIGHT_GRAY_TEXT}
                              fillOpacity={0.25}
                            />
                          );
                        })}
                        {path.length > 0 && (
                          <Polygon
                            paths={path}
                            strokeColor={AppColors.ERROR_TEXT}
                            strokeOpacity={0.9}
                            strokeWeight={2}
                            fillColor={AppColors.ERROR_TEXT}
                            fillOpacity={0.2}
                          />
                        )}
                      </Map>
                    </APIProvider>
                  </Box>
                </Box>
              </Dialog.Body>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
